import React, { useEffect, useRef } from 'react';
import { ArrowRight, Check, Clock, Heart, Shield } from 'lucide-react';
import { PurchasePopup } from '../components/PurchasePopup';
import { TestimonialCard } from '../components/TestimonialCard';
import { BonusCard } from '../components/BonusCard';
export function SalesPage() {
  const checkoutRef = useRef<HTMLDivElement>(null);
  const scrollToCheckout = () => {
    checkoutRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  // Intersection Observer for fade-in animations
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100', 'translate-y-0');
          entry.target.classList.remove('opacity-0', 'translate-y-10');
        }
      });
    }, {
      threshold: 0.1
    });
    document.querySelectorAll('.animate-on-scroll').forEach(el => {
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  return <div className="min-h-screen bg-[#FDFBF7] font-sans text-gray-800 overflow-x-hidden" data-id="element-24">
      {/* Navigation / Top Bar */}
      <nav className="w-full py-4 px-6 flex justify-center bg-white/80 backdrop-blur-sm sticky top-0 z-40 border-b border-orange-100" data-id="element-25">
        <span className="font-serif font-bold text-xl text-orange-900 tracking-wide" data-id="element-26">
          VOLVER EN 7 MINUTOS
        </span>
      </nav>

      {/* Hero Section */}
      <header className="relative pt-12 pb-20 px-4 md:pt-20 md:pb-32 overflow-hidden" data-id="element-27">
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-28">
          <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-orange-200/20 blur-3xl" data-id="element-29"></div>
          <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-purple-200/20 blur-3xl" data-id="element-30"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center animate-on-scroll opacity-0 translate-y-10 transition-all duration-1000" data-id="element-31">
          <h1 className="font-serif text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-32">
            Micro-rituales express para regular la ansiedad cuando no podés
            parar
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-33">
            Seguís funcionando. Desde afuera, todo parece normal. Pero por
            dentro… algo está acelerado.
          </p>

          <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-12" data-id="element-34">
            <div className="relative w-full max-w-sm mx-auto md:mx-0 shadow-2xl rounded-lg transform rotate-[-2deg] hover:rotate-0 transition-transform duration-500" data-id="element-35">
              <img src="https://cdn.magicpatterns.com/uploads/geHPoDo1xxK8yXDDJ5voV7/volver_en_7_minutos_cover.png" alt="Libro Volver en 7 Minutos" className="w-full rounded-lg" onClick={scrollToCheckout} data-id="element-36" />
            </div>
          </div>

          <button onClick={scrollToCheckout} className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-4 px-8 rounded-full text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex items-center gap-2 mx-auto" data-id="element-37">
            Quiero volver a mí <ArrowRight className="w-5 h-5" data-id="element-38" />
          </button>
        </div>
      </header>

      {/* Problem Section */}
      <section className="py-16 px-4 md:py-24 bg-white" data-id="element-39">
        <div className="max-w-2xl mx-auto space-y-8 text-lg leading-relaxed animate-on-scroll opacity-0 translate-y-10 transition-all duration-700" data-id="element-40">
          <p className="font-medium text-orange-800" data-id="element-41">
            No es un ataque de ansiedad. No es un colapso.
          </p>
          <p className="text-2xl font-serif font-bold text-gray-900" data-id="element-42">
            Es algo más silencioso. Es no volver nunca del todo a vos.
          </p>

          <div className="space-y-6 text-gray-700" data-id="element-43">
            <h3 className="text-2xl font-bold text-gray-900 mt-12 mb-4" data-id="element-44">
              Vivir en automático cansa más que correr
            </h3>
            <p data-id="element-45">
              Te levantás y ya estás en marcha. Mensajes. Responsabilidades.
              Decisiones.
            </p>
            <p data-id="element-46">No hay espacio para sentir. Solo para resolver.</p>
            <p data-id="element-47">Y aunque no haya un problema concreto… tu cuerpo no descansa.</p>
            <p data-id="element-48">
              Respirás corto. Reaccionás rápido. Todo te molesta un poco más de
              lo normal.
            </p>
            <p className="font-semibold" data-id="element-49">
              Y lo peor no es eso. Lo peor es empezar a creer que esto es
              normal.
            </p>
          </div>
        </div>
      </section>

      {/* The Lie Section */}
      <section className="py-16 px-4 bg-[#FDFBF7]" data-id="element-50">
        <div className="max-w-2xl mx-auto animate-on-scroll opacity-0 translate-y-10 transition-all duration-700" data-id="element-51">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-8 text-center" data-id="element-52">
            La mentira que nadie dice
          </h2>

          <div className="bg-white p-8 rounded-2xl shadow-sm border border-orange-100 mb-8" data-id="element-53">
            <p className="text-gray-600 italic mb-4" data-id="element-54">Te dijeron:</p>
            <div className="flex flex-wrap gap-3 mb-6" data-id="element-55">
              <span className="bg-gray-100 px-4 py-2 rounded-full text-gray-700" data-id="element-56">
                "Respirá profundo"
              </span>
              <span className="bg-gray-100 px-4 py-2 rounded-full text-gray-700" data-id="element-57">
                "Tomate un rato"
              </span>
              <span className="bg-gray-100 px-4 py-2 rounded-full text-gray-700" data-id="element-58">
                "Tenés que relajarte"
              </span>
            </div>
            <p className="text-gray-800 font-medium" data-id="element-59">
              Pero nadie te explicó cómo volver al cuerpo cuando no podés parar.
            </p>
          </div>

          <p className="text-lg text-gray-700 mb-6" data-id="element-60">
            Porque la ansiedad no aparece cuando todo está tranquilo. Aparece
            cuando seguís… aunque el cuerpo ya pidió auxilio.
          </p>
          <p className="text-lg text-gray-700 font-bold" data-id="element-61">
            Y ahí, las soluciones tradicionales no sirven.
          </p>
        </div>
      </section>

      {/* The Real Problem Section */}
      <section className="py-16 px-4 bg-orange-50/50" data-id="element-62">
        <div className="max-w-3xl mx-auto text-center animate-on-scroll opacity-0 translate-y-10 transition-all duration-700" data-id="element-63">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-12" data-id="element-64">
            El problema no es la ansiedad
          </h2>

          <div className="grid md:grid-cols-3 gap-6 mb-12" data-id="element-65">
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-66">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-500" data-id="element-67">
                <Clock className="w-6 h-6" data-id="element-68" />
              </div>
              <p className="font-medium" data-id="element-69">Nunca cerrás ciclos</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-70">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-500" data-id="element-71">
                <Heart className="w-6 h-6" data-id="element-72" />
              </div>
              <p className="font-medium" data-id="element-73">Nunca bajás la activación</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-74">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-500" data-id="element-75">
                <Shield className="w-6 h-6" data-id="element-76" />
              </div>
              <p className="font-medium" data-id="element-77">Nunca volvés al eje</p>
            </div>
          </div>

          <p className="text-lg text-gray-700 max-w-2xl mx-auto" data-id="element-78">
            Vivís del cuello para arriba. Pensando. Sosteniendo. Aguantando.
            <br data-id="element-79" />
            <br data-id="element-80" />
            <span className="font-bold text-orange-800" data-id="element-81">
              Y ningún pensamiento calma un sistema nervioso acelerado.
            </span>
          </p>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-4 bg-white relative overflow-hidden" data-id="element-82">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-orange-50 to-transparent -z-10" data-id="element-83"></div>

        <div className="max-w-4xl mx-auto animate-on-scroll opacity-0 translate-y-10 transition-all duration-700" data-id="element-84">
          <div className="text-center mb-16" data-id="element-85">
            <h2 className="text-4xl font-serif font-bold text-gray-900 mb-6" data-id="element-86">
              La solución no es más fuerza. Es regulación.
            </h2>
            <p className="text-xl text-gray-600" data-id="element-87">
              Es interrumpir el bucle. Es volver al cuerpo. Es bajar la
              intensidad en minutos, no en horas.
            </p>
          </div>

          <div className="bg-[#FDFBF7] rounded-3xl p-8 md:p-12 shadow-xl border border-orange-100" data-id="element-88">
            <div className="flex flex-col md:flex-row gap-12 items-center" data-id="element-89">
              <div className="w-full md:w-1/2" data-id="element-90">
                <img src="https://cdn.magicpatterns.com/uploads/geHPoDo1xxK8yXDDJ5voV7/volver_en_7_minutos_cover.png" alt="Libro Volver en 7 Minutos" className="w-full rounded-lg shadow-lg transform rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-91" />
              </div>
              <div className="w-full md:w-1/2 space-y-6" data-id="element-92">
                <h3 className="text-2xl font-bold text-gray-900" data-id="element-93">
                  Por eso nació: VOLVER EN 7 MINUTOS
                </h3>
                <p className="text-gray-700" data-id="element-94">
                  Una guía breve y profunda con micro-rituales express de:
                </p>

                <ul className="space-y-4" data-id="element-95">
                  <li className="flex items-start gap-3" data-id="element-96">
                    <div className="bg-orange-100 p-1 rounded text-orange-600 mt-1" data-id="element-97">
                      <Clock className="w-4 h-4" data-id="element-98" />
                    </div>
                    <div data-id="element-99">
                      <span className="font-bold" data-id="element-100">3 minutos</span> — cuando no
                      hay nada de tiempo
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-101">
                    <div className="bg-orange-100 p-1 rounded text-orange-600 mt-1" data-id="element-102">
                      <Clock className="w-4 h-4" data-id="element-103" />
                    </div>
                    <div data-id="element-104">
                      <span className="font-bold" data-id="element-105">5 minutos</span> — cuando
                      necesitás resetear
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-106">
                    <div className="bg-orange-100 p-1 rounded text-orange-600 mt-1" data-id="element-107">
                      <Clock className="w-4 h-4" data-id="element-108" />
                    </div>
                    <div data-id="element-109">
                      <span className="font-bold" data-id="element-110">7 minutos</span> — cuando el
                      cuerpo pide volver
                    </div>
                  </li>
                </ul>

                <div className="pt-4" data-id="element-111">
                  <p className="font-semibold text-orange-800 mb-2" data-id="element-112">
                    Diseñados para:
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-700" data-id="element-113">
                    <div className="flex items-center gap-2" data-id="element-114">
                      <Check className="w-4 h-4 text-green-500" data-id="element-115" /> regular la
                      ansiedad
                    </div>
                    <div className="flex items-center gap-2" data-id="element-116">
                      <Check className="w-4 h-4 text-green-500" data-id="element-117" /> bajar la
                      activación
                    </div>
                    <div className="flex items-center gap-2" data-id="element-118">
                      <Check className="w-4 h-4 text-green-500" data-id="element-119" /> salir del
                      modo automático
                    </div>
                    <div className="flex items-center gap-2" data-id="element-120">
                      <Check className="w-4 h-4 text-green-500" data-id="element-121" /> recuperar
                      presencia
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Not Meditation Section */}
      <section className="py-16 px-4 bg-gray-50" data-id="element-122">
        <div className="max-w-2xl mx-auto text-center animate-on-scroll opacity-0 translate-y-10 transition-all duration-700" data-id="element-123">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-8" data-id="element-124">
            Esto no es meditación
          </h2>

          <div className="grid md:grid-cols-2 gap-8 text-left" data-id="element-125">
            <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-red-400" data-id="element-126">
              <h3 className="font-bold text-lg mb-4" data-id="element-127">No tenés que:</h3>
              <ul className="space-y-2 text-gray-600" data-id="element-128">
                <li className="flex items-center gap-2" data-id="element-129">❌ Vaciar la mente</li>
                <li className="flex items-center gap-2" data-id="element-130">
                  ❌ Sentarte 40 minutos
                </li>
                <li className="flex items-center gap-2" data-id="element-131">❌ "Hacerlo bien"</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-green-400" data-id="element-132">
              <h3 className="font-bold text-lg mb-4" data-id="element-133">Solo necesitás:</h3>
              <ul className="space-y-2 text-gray-600" data-id="element-134">
                <li className="flex items-center gap-2" data-id="element-135">✔ seguir el ritual</li>
                <li className="flex items-center gap-2" data-id="element-136">
                  ✔ permitir que el cuerpo baje
                </li>
                <li className="flex items-center gap-2" data-id="element-137">✔ volver</li>
              </ul>
            </div>
          </div>

          <p className="mt-8 text-lg font-medium text-gray-800" data-id="element-138">Nada más.</p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-white" data-id="element-139">
        <div className="max-w-3xl mx-auto text-center animate-on-scroll opacity-0 translate-y-10 transition-all duration-700" data-id="element-140">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-8" data-id="element-141">
            ¿Qué cambia cuando volvés?
          </h2>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-142">
            {['Respirás distinto', 'Pensás con más claridad', 'Respondés en lugar de reaccionar', 'Recuperás sensibilidad', 'Sentís alivio inmediato'].map((benefit, i) => <span key={i} className="bg-orange-50 text-orange-800 px-6 py-3 rounded-full font-medium border border-orange-100" data-id="element-143">
                {benefit}
              </span>)}
          </div>
          <p className="mt-8 text-xl font-serif italic text-gray-600" data-id="element-144">
            No es magia. Es fisiología + presencia.
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-[#FDFBF7]" data-id="element-145">
        <div className="max-w-6xl mx-auto" data-id="element-146">
          <h2 className="text-3xl font-serif font-bold text-center text-gray-900 mb-12" data-id="element-147">
            Historias de quienes ya volvieron
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 animate-on-scroll opacity-0 translate-y-10 transition-all duration-700" data-id="element-148">
            <TestimonialCard name="Lucía" age={32} location="Buenos Aires" quote="Siempre funcionaba en piloto automático, sin darme cuenta de que mi cuerpo pedía parar. Los rituales de 7 minutos me devolvieron la capacidad de sentir alivio inmediato." data-id="element-149" />
            <TestimonialCard name="Sofía" age={38} location="Córdoba" quote="No me identificaba como ansiosa, pero vivía acelerada todo el tiempo. Este libro me enseñó que volver a mí no requiere horas, solo intención." data-id="element-150" />
            <TestimonialCard name="Valentina" age={29} location="Rosario" quote="El cuerpo me pedía auxilio pero no sabía cómo parar sin dejar todo. Los micro-rituales me dieron una salida real en medio del caos diario." data-id="element-151" />
            <TestimonialCard name="Camila" age={41} location="Mendoza" quote="Pensaba que era normal vivir así, siempre tensa. Descubrí que puedo regular mi sistema nervioso en minutos, no en semanas de terapia." data-id="element-152" />
            <TestimonialCard name="Martina" age={35} location="La Plata" quote="Necesitaba algo que funcionara hoy, no promesas a largo plazo. Los rituales de 3, 5 y 7 minutos son mi salvavidas diario." data-id="element-153" />
          </div>
        </div>
      </section>

      {/* Who is this for Section */}
      <section className="py-16 px-4 bg-white" data-id="element-154">
        <div className="max-w-4xl mx-auto animate-on-scroll opacity-0 translate-y-10 transition-all duration-700" data-id="element-155">
          <h2 className="text-3xl font-serif font-bold text-center text-gray-900 mb-12" data-id="element-156">
            Para quién es este producto
          </h2>

          <div className="grid md:grid-cols-2 gap-12" data-id="element-157">
            <div data-id="element-158">
              <h3 className="text-xl font-bold text-green-700 mb-6 flex items-center gap-2" data-id="element-159">
                <Check className="w-6 h-6" data-id="element-160" /> Este producto es para vos si:
              </h3>
              <ul className="space-y-4 text-gray-700" data-id="element-161">
                <li className="flex items-start gap-3" data-id="element-162">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2" data-id="element-163"></span>
                  sentís que nunca terminás de parar
                </li>
                <li className="flex items-start gap-3" data-id="element-164">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2" data-id="element-165"></span>
                  seguís funcionando aunque estés agotada
                </li>
                <li className="flex items-start gap-3" data-id="element-166">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2" data-id="element-167"></span>
                  no te identificás como "ansiosa", pero vivís acelerada
                </li>
                <li className="flex items-start gap-3" data-id="element-168">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2" data-id="element-169"></span>
                  no tenés tiempo ni energía para procesos largos
                </li>
                <li className="flex items-start gap-3" data-id="element-170">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2" data-id="element-171"></span>
                  necesitás algo que funcione hoy
                </li>
              </ul>
            </div>

            <div data-id="element-172">
              <h3 className="text-xl font-bold text-red-700 mb-6 flex items-center gap-2" data-id="element-173">
                <span className="text-xl" data-id="element-174">❌</span> No es para vos si buscás:
              </h3>
              <ul className="space-y-4 text-gray-700" data-id="element-175">
                <li className="flex items-start gap-3" data-id="element-176">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2" data-id="element-177"></span>
                  soluciones mágicas
                </li>
                <li className="flex items-start gap-3" data-id="element-178">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2" data-id="element-179"></span>
                  promesas irreales
                </li>
                <li className="flex items-start gap-3" data-id="element-180">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2" data-id="element-181"></span>
                  evadir lo que sentís
                </li>
              </ul>
              <p className="mt-8 font-medium text-gray-900 italic border-l-4 border-orange-400 pl-4" data-id="element-182">
                Esto no es para tapar. Es para volver.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-16 px-4 bg-orange-50" data-id="element-183">
        <div className="max-w-2xl mx-auto text-center animate-on-scroll opacity-0 translate-y-10 transition-all duration-700" data-id="element-184">
          <h2 className="text-2xl font-serif font-bold text-gray-900 mb-6" data-id="element-185">
            De dónde nace VOLVER EN 7 MINUTOS
          </h2>
          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-186">
            <p data-id="element-187">
              Este producto no nació de un libro. Nació de un cuerpo cansado.
            </p>
            <p data-id="element-188">
              De momentos donde no había tiempo para nada… pero el cuerpo pedía
              auxilio.
            </p>
            <p data-id="element-189">De descubrir que no hacía falta más esfuerzo, sino menos.</p>
            <p data-id="element-190">
              De entender que volver no lleva horas. Lleva intención y
              dirección.
            </p>
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section className="py-20 px-4 bg-white" data-id="element-191">
        <div className="max-w-6xl mx-auto" data-id="element-192">
          <div className="text-center mb-16" data-id="element-193">
            <span className="bg-orange-100 text-orange-800 px-4 py-1 rounded-full text-sm font-bold tracking-wide uppercase mb-4 inline-block" data-id="element-194">
              Regalos Exclusivos
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900" data-id="element-195">
              Llevate estos bonos GRATIS hoy
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-10 justify-center animate-on-scroll opacity-0 translate-y-10 transition-all duration-700" data-id="element-196">
            <BonusCard imageSrc="https://cdn.magicpatterns.com/uploads/cM4izhvXCqeaLFcMrEYS87/BONO_1.png" title="Creencias Ocultas" description="Las frases internas que te mantienen acelerada sin que lo sepas." data-id="element-197" />
            <BonusCard imageSrc="https://cdn.magicpatterns.com/uploads/mosEkJcg2VzLLenDQ5hSxf/BONO_2.png" title="La Raíz" description="Qué está intentando proteger tu ansiedad. Un texto para comprender, no para juzgar." data-id="element-198" />
            <BonusCard imageSrc="https://cdn.magicpatterns.com/uploads/dbCNCJMkAouSvc8buNKBvk/BONO_3.png" title="Calendario de Vuelta" description="7 días para volver al cuerpo y detectar el patrón." data-id="element-199" />
          </div>
        </div>
      </section>

      {/* Promise Section */}
      <section className="py-16 px-4 bg-[#FDFBF7]" data-id="element-200">
        <div className="max-w-2xl mx-auto text-center animate-on-scroll opacity-0 translate-y-10 transition-all duration-700" data-id="element-201">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-6" data-id="element-202">
            La promesa
          </h2>
          <p className="text-xl text-gray-700 mb-4" data-id="element-203">
            No te prometo una vida sin estrés.
          </p>
          <p className="text-xl font-bold text-orange-800" data-id="element-204">
            Te prometo volver a vos en 3, 5 o 7 minutos, incluso cuando todo
            sigue igual.
          </p>
        </div>
      </section>

      {/* Checkout Section */}
      <section id="checkout" ref={checkoutRef} className="py-20 px-4 bg-white relative" data-id="element-205">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-orange-50 to-purple-50 rounded-3xl p-8 md:p-16 shadow-2xl border border-white animate-on-scroll opacity-0 translate-y-10 transition-all duration-700" data-id="element-206">
          <div className="text-center mb-10" data-id="element-207">
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 mb-6" data-id="element-208">
              Empezá a volver hoy mismo
            </h2>
            <div className="flex flex-col items-center gap-4 mb-8" data-id="element-209">
              <div className="flex items-center gap-4" data-id="element-210">
                <span className="text-2xl text-gray-400 line-through font-medium" data-id="element-211">
                  $6.999
                </span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-bold" data-id="element-212">
                  GRATIS por el día de hoy (Bonos)
                </span>
              </div>
              <div className="text-6xl font-bold text-gray-900 tracking-tight" data-id="element-213">
                $13.999
              </div>
              <p className="text-gray-500 text-sm" data-id="element-214">
                Pago único. Acceso de por vida.
              </p>
            </div>

            <a href="https://checkout.gaiadigitalpro.com/checkouts/cn/hWN7eC7YOIDB9KHguJcS1a2z/es-ar?_r=AQABzZ-eTTMb18s3FYOr12sslMZX4Vq5lHFbi1v_pwObB_s&preview_theme_id=163059597347" target="_blank" rel="noopener noreferrer" className="inline-block w-full max-w-md bg-orange-600 hover:bg-orange-700 text-white font-bold py-5 px-8 rounded-full text-xl shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300" data-id="element-215">
              Descargar VOLVER EN 7 MINUTOS
            </a>
          </div>

          <div className="grid md:grid-cols-3 gap-6 text-sm text-gray-600 border-t border-gray-200 pt-8" data-id="element-216">
            <div className="flex items-center justify-center gap-2" data-id="element-217">
              <Check className="w-5 h-5 text-green-500" data-id="element-218" />
              <span data-id="element-219">Acceso inmediato</span>
            </div>
            <div className="flex items-center justify-center gap-2" data-id="element-220">
              <Check className="w-5 h-5 text-green-500" data-id="element-221" />
              <span data-id="element-222">Compra 100% segura</span>
            </div>
            <div className="flex items-center justify-center gap-2" data-id="element-223">
              <Check className="w-5 h-5 text-green-500" data-id="element-224" />
              <span data-id="element-225">Soporte dedicado</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 text-center" data-id="element-226">
        <div className="max-w-4xl mx-auto" data-id="element-227">
          <h3 className="font-serif font-bold text-2xl mb-6" data-id="element-228">
            VOLVER EN 7 MINUTOS
          </h3>
          <div className="flex flex-col md:flex-row justify-center gap-6 text-gray-400 text-sm mb-8" data-id="element-229">
            <p data-id="element-230">👉 Descargá VOLVER EN 7 MINUTOS ahora</p>
            <p data-id="element-231">👉 Tenelo a mano para cuando el cuerpo lo pida</p>
            <p data-id="element-232">👉 Volvé a vos, sin esperar a que todo se calme</p>
          </div>
          <p className="text-gray-600 text-xs" data-id="element-233">
            © {new Date().getFullYear()} Volver en 7 Minutos. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      <PurchasePopup data-id="element-234" />
    </div>;
}